/**
 * Renders the subset of Markdown an LLM reply realistically contains — headings, paragraphs, bullet and
 * numbered lists, fenced code, bold/italic/inline code and links — to HTML. Everything is HTML-escaped
 * first, so model output can never inject markup. Hand-rolled because no Markdown library is in
 * package-lock.json and the chat must not pull in a new dependency.
 */
export function renderMarkdown(markdown: string): string {
  const blocks: string[] = [];
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim() === '') {
      i++;
      continue;
    }

    if (isFence(line)) {
      const code: string[] = [];
      i++;
      while (i < lines.length && !isFence(lines[i])) {
        code.push(lines[i]);
        i++;
      }
      i++; // closing fence (or end of input while the fence is still streaming)
      blocks.push(`<pre><code>${escapeHtml(code.join('\n'))}</code></pre>`);
      continue;
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      const level = heading[1].length;
      blocks.push(`<h${level}>${renderInline(heading[2])}</h${level}>`);
      i++;
      continue;
    }

    if (isBullet(line) || isNumbered(line)) {
      const ordered = isNumbered(line);
      const matches = ordered ? isNumbered : isBullet;
      const marker = ordered ? /^\s*\d+[.)]\s+/ : /^\s*[-*+]\s+/;
      const items: string[] = [];
      while (i < lines.length && matches(lines[i])) {
        items.push(`<li>${renderInline(lines[i].replace(marker, ''))}</li>`);
        i++;
      }
      const tag = ordered ? 'ol' : 'ul';
      blocks.push(`<${tag}>${items.join('')}</${tag}>`);
      continue;
    }

    const paragraph: string[] = [];
    while (i < lines.length && lines[i].trim() !== '' && !startsBlock(lines[i])) {
      paragraph.push(lines[i]);
      i++;
    }
    blocks.push(`<p>${paragraph.map(renderInline).join('<br>')}</p>`);
  }

  return blocks.join('');
}

function isFence(line: string): boolean {
  return /^```/.test(line);
}

function isBullet(line: string): boolean {
  return /^\s*[-*+]\s+/.test(line);
}

function isNumbered(line: string): boolean {
  return /^\s*\d+[.)]\s+/.test(line);
}

function startsBlock(line: string): boolean {
  return isFence(line) || /^#{1,6}\s+/.test(line) || isBullet(line) || isNumbered(line);
}

function renderInline(text: string): string {
  // Inline code is lifted out first so emphasis markers inside it are left alone; the placeholder
  // uses characters escapeHtml never produces and Markdown never treats as syntax.
  const codeSpans: string[] = [];
  let html = escapeHtml(text).replace(/`([^`]+)`/g, (_, code) => {
    codeSpans.push(`<code>${code}</code>`);
    return ` ${codeSpans.length - 1} `;
  });

  html = html
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/__(.+?)__/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*\s][^*]*?)\*/g, '$1<em>$2</em>')
    .replace(/(^|[^_\w])_([^_\s][^_]*?)_(?!\w)/g, '$1<em>$2</em>')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');

  return html.replace(/ (\d+) /g, (_, index) => codeSpans[Number(index)]);
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
